import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Alert, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Chip, TablePagination, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, Tooltip, Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Checkbox from '@mui/material/Checkbox';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  last_login: string | null;
  pwa_installed?: boolean;
  pwa_platform?: string | null;
}

interface TokenPayload {
  user_id: number;
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface DeletedAccount {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  deletion_type: 'soft' | 'hard';
  deletion_type_display: string;
  deleted_by: 'self' | 'admin';
  deleted_by_display: string;
  deleted_by_username: string | null;
  deleted_at: string;
}

interface EmailPreview {
  user_id: number;
  username: string;
  email: string;
  first_name: string;
  subject: string;
  preview_text: string;
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Current user permissions
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  // User list state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Search and pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    is_active: true,
    is_staff: false,
    is_superuser: false,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Reset email dialog state
  const [resetEmailDialogOpen, setResetEmailDialogOpen] = useState(false);
  const [resetEmailUser, setResetEmailUser] = useState<User | null>(null);
  const [emailPreview, setEmailPreview] = useState<EmailPreview | null>(null);
  const [resetEmailLoading, setResetEmailLoading] = useState(false);
  const [resetEmailError, setResetEmailError] = useState<string | null>(null);

  // Create user dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    send_reset_email: true,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Deleted accounts state
  const [deletedAccounts, setDeletedAccounts] = useState<DeletedAccount[]>([]);
  const [deletedAccountsLoading, setDeletedAccountsLoading] = useState(false);
  const [deletedAccountsOpen, setDeletedAccountsOpen] = useState(false);

  // Load current user permissions
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = jwtDecode<TokenPayload>(token);
        setCurrentUserId(payload.user_id);
        setIsSuperuser(payload.is_superuser || false);
      } catch (err) {
        console.error('Failed to decode token:', err);
      }
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        per_page: String(rowsPerPage),
      });
      if (search) {
        params.append('search', search);
      }

      const data = await adminFetch(`${apiUrl}/api/admin/users/?${params}`);
      setUsers(data.users);
      setTotalUsers(data.total);
    } catch (err: unknown) {
      const error = err as Error;
      // Don't show error if redirected for 2FA
      if (!error.message?.includes('Admin authentication')) {
        setError(error.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, page, rowsPerPage, search, adminFetch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check if current user can edit this user
  const canEditUser = (user: User) => {
    // Staff cannot edit superusers
    if (!isSuperuser && user.is_superuser) return false;
    return true;
  };

  // Check if current user can delete this user
  const canDeleteUser = (user: User) => {
    // Cannot delete yourself
    if (user.id === currentUserId) return false;
    // Staff cannot delete superusers
    if (!isSuperuser && user.is_superuser) return false;
    return true;
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      is_active: user.is_active,
      is_staff: user.is_staff,
      is_superuser: user.is_superuser,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;

    setEditLoading(true);
    setEditError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/users/${editingUser.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });

      setEditDialogOpen(false);
      setSuccessMessage('User updated successfully');
      setTimeout(() => setSuccessMessage(null), 4000);
      loadUsers();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to update user');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;

    setDeleteLoading(true);

    try {
      await adminFetch(`${apiUrl}/api/admin/users/${deletingUser.id}/`, {
        method: 'DELETE',
      });

      setDeleteDialogOpen(false);
      setSuccessMessage(`User ${deletingUser.username} deleted successfully`);
      setTimeout(() => setSuccessMessage(null), 4000);
      loadUsers();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setError(error.message || 'Failed to delete user');
      }
    } finally {
      setDeleteLoading(false);
      setDeletingUser(null);
    }
  };

  // Password functions
  const handlePasswordClick = (user: User) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordDialogOpen(true);
  };

  const handlePasswordSave = async () => {
    if (!passwordUser) return;

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    setPasswordError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/users/${passwordUser.id}/set-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });

      setPasswordDialogOpen(false);
      setSuccessMessage(`Password updated for ${passwordUser.username}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setPasswordError(error.message || 'Failed to set password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  // Reset email functions
  const handleResetEmailClick = async (user: User) => {
    setResetEmailUser(user);
    setEmailPreview(null);
    setResetEmailError(null);
    setResetEmailDialogOpen(true);

    try {
      const preview = await adminFetch(`${apiUrl}/api/admin/users/${user.id}/send-reset-email/`);
      setEmailPreview(preview);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setResetEmailError(error.message || 'Failed to load email preview');
      }
    }
  };

  const handleSendResetEmail = async () => {
    if (!resetEmailUser) return;

    setResetEmailLoading(true);
    setResetEmailError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/users/${resetEmailUser.id}/send-reset-email/`, {
        method: 'POST',
      });

      setResetEmailDialogOpen(false);
      setSuccessMessage(`Password reset email sent to ${resetEmailUser.email}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setResetEmailError(error.message || 'Failed to send reset email');
      }
    } finally {
      setResetEmailLoading(false);
    }
  };

  // Create user functions
  const handleOpenCreateDialog = () => {
    setCreateForm({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      send_reset_email: true,
    });
    setCreateStep(1);
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateStep(1);
    setCreateError(null);
  };

  const handleCreateNext = () => {
    // Validate form before going to confirmation
    if (!createForm.username.trim()) {
      setCreateError('Username is required');
      return;
    }
    if (!createForm.email.trim()) {
      setCreateError('Email is required');
      return;
    }
    if (!createForm.first_name.trim()) {
      setCreateError('First name is required');
      return;
    }
    if (!createForm.last_name.trim()) {
      setCreateError('Last name is required');
      return;
    }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      setCreateError('Please enter a valid email address');
      return;
    }
    // Basic username validation (alphanumeric)
    if (!/^[a-zA-Z0-9]+$/.test(createForm.username)) {
      setCreateError('Username can only contain letters and numbers');
      return;
    }
    setCreateError(null);
    setCreateStep(2);
  };

  const handleCreateBack = () => {
    setCreateStep(1);
    setCreateError(null);
  };

  const handleCreateUser = async () => {
    setCreateLoading(true);
    setCreateError(null);

    try {
      const response = await adminFetch(`${apiUrl}/api/admin/users/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: createForm.username.trim(),
          email: createForm.email.trim(),
          first_name: createForm.first_name.trim(),
          last_name: createForm.last_name.trim(),
          send_reset_email: createForm.send_reset_email,
        }),
      });

      setCreateDialogOpen(false);
      setSuccessMessage(response.message || `User ${createForm.username} created successfully`);
      setTimeout(() => setSuccessMessage(null), 4000);
      loadUsers();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setCreateError(error.message || 'Failed to create user');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const loadDeletedAccounts = async () => {
    setDeletedAccountsLoading(true);
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/users/deleted/`);
      setDeletedAccounts(data.deleted_accounts);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        console.error('Failed to load deleted accounts:', error.message);
      }
    } finally {
      setDeletedAccountsLoading(false);
    }
  };

  const handleToggleDeletedAccounts = () => {
    const willOpen = !deletedAccountsOpen;
    setDeletedAccountsOpen(willOpen);
    if (willOpen && deletedAccounts.length === 0) {
      loadDeletedAccounts();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">User Management</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by username, email, or name..."
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>PWA</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.first_name} {user.last_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.is_superuser ? (
                      <Chip label="Superuser" color="error" size="small" />
                    ) : user.is_staff ? (
                      <Chip label="Staff" color="primary" size="small" />
                    ) : (
                      <Chip label="User" variant="outlined" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {user.pwa_installed ? (
                      <Tooltip title={user.pwa_platform ? `Platform: ${user.pwa_platform}` : 'Installed'}>
                        <Chip
                          icon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />}
                          label={user.pwa_platform ? user.pwa_platform.charAt(0).toUpperCase() + user.pwa_platform.slice(1) : 'Yes'}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="No" variant="outlined" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.date_joined).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={canEditUser(user) ? "Edit user" : "Cannot edit superuser"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(user)}
                          disabled={!canEditUser(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canEditUser(user) ? "Set password" : "Cannot modify superuser"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handlePasswordClick(user)}
                          disabled={!canEditUser(user)}
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Send password reset email">
                      <IconButton
                        size="small"
                        onClick={() => handleResetEmailClick(user)}
                        disabled={!user.email}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={canDeleteUser(user) ? "Delete user" : (user.id === currentUserId ? "Cannot delete yourself" : "Cannot delete superuser")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(user)}
                          disabled={!canDeleteUser(user)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Deleted Accounts Section */}
      <Box sx={{ mt: 4 }}>
        <Button
          variant="text"
          onClick={handleToggleDeletedAccounts}
          startIcon={<DeleteIcon />}
          endIcon={deletedAccountsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mb: 1, textTransform: 'none' }}
        >
          <Typography variant="h6" component="span">
            Deleted Accounts
          </Typography>
        </Button>
        <Collapse in={deletedAccountsOpen}>
          {deletedAccountsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : deletedAccounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              No deleted accounts found.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Deleted By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>{account.email}</TableCell>
                      <TableCell>{account.username}</TableCell>
                      <TableCell>{account.first_name} {account.last_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={account.deletion_type_display}
                          color={account.deletion_type === 'hard' ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {account.deleted_by_display}
                        {account.deleted_by_username && ` (${account.deleted_by_username})`}
                      </TableCell>
                      <TableCell>
                        {new Date(account.deleted_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Box>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {editingUser?.username}</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="First Name"
              value={editForm.first_name}
              onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={editForm.last_name}
              onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <Divider />
            <Typography variant="body2" color="text.secondary">
              {isSuperuser ? 'Privilege settings (superuser only):' : 'Privilege settings (view only - superuser required to modify):'}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_staff}
                  onChange={(e) => setEditForm({ ...editForm, is_staff: e.target.checked })}
                  disabled={!isSuperuser || editingUser?.id === currentUserId}
                />
              }
              label="Staff"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_superuser}
                  onChange={(e) => setEditForm({ ...editForm, is_superuser: e.target.checked })}
                  disabled={!isSuperuser || editingUser?.id === currentUserId}
                />
              }
              label="Superuser"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{deletingUser?.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Password for {passwordUser?.username}</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={passwordLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handlePasswordSave}
            disabled={passwordLoading || newPassword.length < 8 || newPassword !== confirmPassword}
          >
            {passwordLoading ? <CircularProgress size={24} /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Reset Email Dialog */}
      <Dialog open={resetEmailDialogOpen} onClose={() => setResetEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Password Reset Email</DialogTitle>
        <DialogContent>
          {resetEmailError && (
            <Alert severity="error" sx={{ mb: 2 }}>{resetEmailError}</Alert>
          )}
          {!emailPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                The following email will be sent to:
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>{emailPreview.email}</strong>
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Subject:
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {emailPreview.subject}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Message Preview:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {emailPreview.preview_text}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetEmailDialogOpen(false)} disabled={resetEmailLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSendResetEmail}
            disabled={resetEmailLoading || !emailPreview}
            startIcon={<EmailIcon />}
          >
            {resetEmailLoading ? <CircularProgress size={24} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createStep === 1 ? 'Add New User' : 'Confirm New User'}
        </DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
          )}

          {createStep === 1 ? (
            // Step 1: Form
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Username"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                fullWidth
                required
                helperText="Letters and numbers only"
              />
              <TextField
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="First Name"
                value={createForm.first_name}
                onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={createForm.last_name}
                onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                fullWidth
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createForm.send_reset_email}
                    onChange={(e) => setCreateForm({ ...createForm, send_reset_email: e.target.checked })}
                  />
                }
                label="Send password reset email when created"
              />
              <Typography variant="body2" color="text.secondary">
                {createForm.send_reset_email
                  ? 'User will be active immediately and receive an email to set their password.'
                  : 'User will be created but inactive until manually activated.'}
              </Typography>
            </Box>
          ) : (
            // Step 2: Confirmation
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Please confirm the details for the new user:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Username:</Typography>
                  <Typography variant="body2"><strong>{createForm.username}</strong></Typography>

                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography variant="body2"><strong>{createForm.email}</strong></Typography>

                  <Typography variant="body2" color="text.secondary">First Name:</Typography>
                  <Typography variant="body2"><strong>{createForm.first_name}</strong></Typography>

                  <Typography variant="body2" color="text.secondary">Last Name:</Typography>
                  <Typography variant="body2"><strong>{createForm.last_name}</strong></Typography>

                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Typography variant="body2">
                    <Chip
                      label={createForm.send_reset_email ? 'Active' : 'Inactive'}
                      color={createForm.send_reset_email ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>

                  <Typography variant="body2" color="text.secondary">Reset Email:</Typography>
                  <Typography variant="body2">
                    {createForm.send_reset_email ? 'Will be sent' : 'Not sending'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={createLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {createStep === 1 ? (
            <Button variant="contained" onClick={handleCreateNext}>
              Next
            </Button>
          ) : (
            <>
              <Button onClick={handleCreateBack} disabled={createLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateUser}
                disabled={createLoading}
                startIcon={createLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {createLoading ? 'Creating...' : 'Create User'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;
